import {
  type IconDefinition,
  faHome,
  faChartArea,
  faExclamationTriangle,
  faDatabase,
  faPieChart,
  faCamera,
  faHeartPulse,
  faFolderTree,
  faServer,
} from '@fortawesome/free-solid-svg-icons';

export type MenuEntry = {
  title: string;
  icon?: IconDefinition;
  hpVmware?: boolean;
  reload?: boolean;
  content: { href: string } | { key: symbol; children: MenuEntry[] };
};

export const submenu = (...children: MenuEntry[]) => ({
  key: Symbol(),
  children,
});

export const menus: Record<string, MenuEntry[]> = {
  hp: [
    {
      title: 'Dashboard',
      icon: faHome,
      content: { href: '/' },
    },
    {
      title: 'Global Statistics',
      icon: faChartArea,
      content: submenu(
        {
          title: 'Performance',
          content: { href: '/hp/performance' },
        },
        {
          title: 'Physical Capacity',
          content: { href: '/hp/physical-capacity' },
        },
        {
          title: 'Logical Capacity',
          content: { href: '/hp/logical-capacity' },
        },
        {
          title: 'VMware Capacity',
          content: { href: '/hp/vmware-capacity' },
          hpVmware: true,
        },
        {
          title: 'Imbalances',
          content: submenu(
            {
              title: 'Throughput',
              content: { href: '/hp/throughput-imbalances' },
            },
            {
              title: 'Latency',
              content: { href: '/hp/latency-imbalances' },
            },
          ),
        },
      ),
    },
    {
      title: 'Events',
      icon: faExclamationTriangle,
      content: submenu(
        {
          title: 'SLA',
          content: { href: '/hp/sla' },
        },
        {
          title: 'Parity Group',
          content: { href: '/hp/pg-events' },
        },
        {
          title: 'FE Port',
          content: { href: '/hp/fe-port-events' },
        },
      ),
    },
  ],
  emc: [
    {
      title: 'Dashboard',
      icon: faHome,
      content: { href: '/' },
    },
    {
      title: 'Global Statistics',
      icon: faChartArea,
      content: submenu(
        {
          title: 'Performance',
          content: { href: '/emc/performance' },
        },
        {
          title: 'Physical Capacity',
          content: { href: '/emc/capacity' },
        },
        {
          title: 'VMware Capacity',
          content: { href: '/emc/vmware' },
        },
        {
          title: 'Imbalances',
          content: { href: '/emc/imbalance' },
        },
      ),
    },
  ],
  netapp: [
    {
      title: 'Capacity statistics',
      icon: faChartArea,
      content: submenu(
        {
          title: 'Aggregates',
          icon: faPieChart,
          content: { href: '/netapp/aggregateCapacity' },
        },
        {
          title: 'Volumes',
          icon: faDatabase,
          content: { href: '/netapp/volumeCapacity' },
        },
        {
          title: 'Qtree',
          icon: faFolderTree,
          content: {
            href: '/netapp/qtrees',
          },
        },
      ),
    },
    {
      title: 'Host exports',
      icon: faServer,
      content: {
        href: '/netapp/hostExports',
      },
    },
    {
      title: 'Shares and Snapshots',
      icon: faCamera,
      content: {
        href: '/netapp/sharesAndSnapshots',
      },
    },
    {
      title: 'Snapmirror Health',
      icon: faHeartPulse,
      content: {
        href: '/netapp/snapMirrorHealth',
      },
    },
  ],
};

export const systemSubmenus: Record<string, (maintainerId: string) => MenuEntry[]> = {
  hp: (system) => [
    { title: 'Dashboard', content: { href: `/overview/${system}/dashboard` } },
    { title: 'DP Pool Board and SLA', content: { href: `/overview/${system}/dpSla` } },
    { title: 'Cache Board', content: { href: `/overview/${system}/cache` } },
    { title: 'CHB and FE Ports', content: { href: `/overview/${system}/adapters` } },
    { title: 'Trends', content: { href: `/overview/${system}/trends` } },
    {
      title: 'LDEV Heatmap',
      content: { href: `/overview/${system}/ldev-heatmap` },
      reload: true,
    },
  ],
  emc: (system) => [
    { title: 'Dashboard', content: { href: `/overview/${system}/emcDashBoard` } },
    { title: 'Host Analysis', content: { href: `/overview/${system}/emcHostAnalysis` } },
    { title: 'Trends', content: { href: `/overview/${system}/emcTrendsBoard` } },
  ],
  netapp: (system) => [
    { title: 'Dashboard', content: { href: `/overview/${system}/netappDashBoard` } },
  ],
};
